import { Box, IconButton, Typography, Link } from "@mui/material";
import React, { useState, useContext } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { DiscoverContext } from "../../Discover/DiscoverContext";

const safeTrim = (s) => (typeof s === "string" ? s.trim() : "");
const dateOnly = (dt) => {
  if (!dt) return "";
  return safeTrim(dt.split(",")[0]);
};
const timeOnly = (dt) => {
  if (!dt) return "";
  const parts = dt.split(",");
  return safeTrim(parts[1] || "");
};

const getTimezoneAbbrev = (tzString) => {
  if (!tzString || typeof tzString !== "string") return "";

  const offsetMatch =
    tzString.match(/UTC\s*([+-]\d{1,2}(?::\d{2})?)/i) ||
    tzString.match(/\(UTC([+-]\d{1,2}(?::\d{2})?)\)/i);
  const offset = offsetMatch ? offsetMatch[1] : null;

  const normOffset = (() => {
    if (!offset) return null;
    const parts = offset.split(":");
    if (parts.length === 1) {
      const hh = parts[0].padStart(3, "0");
      const sign = hh[0];
      const h = hh.slice(1).padStart(2, "0");
      return `${sign}${h}:00`;
    } else {
      const sign = parts[0][0];
      const h = parts[0].slice(1).padStart(2, "0");
      const m = parts[1].padStart(2, "0");
      return `${sign}${h}:${m}`;
    }
  })();

  const map = {
    "+05:30": "IST",
    "+04:00": "GST",
    "-08:00": "PT",
    "-07:00": "MT",
    "-06:00": "CT",
    "-05:00": "ET",
    "+01:00": "CET",
    "+00:00": "UTC",
    "+09:00": "JST",
    "+08:00": "CST",
    "+10:00": "AEST",
    "+03:00": "MSK",
  };

  if (normOffset && map[normOffset]) return map[normOffset];

  const lowered = tzString.toLowerCase();
  if (
    lowered.includes("india") ||
    lowered.includes("kolkata") ||
    lowered.includes("chennai") ||
    lowered.includes("new delhi")
  )
    return "IST";
  if (lowered.includes("gulf") || lowered.includes("dubai")) return "GST";
  if (lowered.includes("pacific") || lowered.includes("los angeles"))
    return "PT";
  if (lowered.includes("united kingdom") || lowered.includes("london"))
    return "GMT";
  if (lowered.includes("eastern") || lowered.includes("new york")) return "ET";

  return normOffset ? `UTC${normOffset}` : "";
};

const EventDisplay = ({
  eventPost,
  name,
  address,
  event_type,
  start_date_time,
  free,
  event_description,
  end_date_time,
  timezone,
  pricing,
  discounted_price,
  show = true,
  actionType,
  EventModalOpener,
  updateEventPost,
  postId,
}) => {
  const { MarkInterested } = useContext(DiscoverContext);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const description = event_description || eventPost?.event_description || "";

  const toggleDescription = () => {
    setShowFullDescription((prev) => !prev);
  };

  const rawStart = start_date_time || eventPost?.start_date_time || "";
  const rawEnd = end_date_time || eventPost?.end_date_time || "";
  const rawTZ = timezone || eventPost?.timezone || "";

  const startDatePart = rawStart ? (rawStart.slice(0, 11) || dateOnly(rawStart)) : "";
  const startTimePart = timeOnly(rawStart) || (rawStart ? rawStart.slice(12, 20) : "");

  const endDatePart = rawEnd ? (rawEnd.slice(0, 11) || dateOnly(rawEnd)) : "";
  const endTimePart = timeOnly(rawEnd) || (rawEnd ? rawEnd.slice(12, 20) : "");

  const tzAbbrev = getTimezoneAbbrev(rawTZ);

  return (
    <Box sx={{ p: "16px 24px" }}>
      {show === true ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <IconButton
            disableRipple
            onClick={EventModalOpener}
            sx={{ background: "#666", padding: "5px" }}
          >
            <EditIcon fontSize="small" sx={{ color: "#fff" }} />
          </IconButton>
          {actionType !== "EDIT" && (
            <IconButton
              disableRipple
              onClick={() => updateEventPost(null)}
              sx={{ background: "#666", ml: 1, padding: "5px" }}
            >
              <CloseIcon fontSize="small" sx={{ color: "#fff" }} />
            </IconButton>
          )}
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: { xs: "60%", sm: "70%" } }}>
          <Typography
            sx={{
              fontSize: "17px",
              fontWeight: 600,
              lineHeight: "25px",
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {name ? name : eventPost?.name}
            <Box
              sx={{
                ml: { xs: 0, sm: 1 },
                background: "#d7eefc",
                p: "1px 10px",
                borderRadius: "10px",
                fontSize: { xs: "10px", sm: "13px" },
                mt: { xs: 1, sm: 0 },
                minWidth:
                  event_type === "INPERSON" ||
                  eventPost?.event_type === "INPERSON"
                    ? { xs: 0, sm: "76px" }
                    : { xs: 0, sm: "60px" },
              }}
            >
              {event_type
                ? event_type === "INPERSON"
                  ? "In Person"
                  : "Online"
                : eventPost?.event_type === "INPERSON"
                ? "In Person"
                : "Online"}
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "15px",
              display: "flex",
              alignItems: "flex-start",
              mt: { xs: 1, sm: 0.5 },
              color: "#606060",
            }}
          >
            <img
              src="/Image/DashImage/RightSidebar/connect_icon.svg"
              alt="fire icon"
              style={{
                width: "9px",
                filter: "grayscale(100%)",
                marginRight: 5,
              }}
            />
            {address ? address : eventPost?.address}
          </Typography>
        </Box>
        <Box
          sx={{
            p: "7px 12px",
            background: "#d71a62",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            onClick={() => MarkInterested(postId)}
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "20px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <img
              src="/Image/Discover/Sale/Asset 1.svg"
              alt="message icon"
              style={{
                width: "17px",
                marginRight: 8,
              }}
            />
            Interested
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <img
              src="/Image/Discover/Events/Asset 3.svg"
              alt="time icon"
              style={{ width: "22px" }}
            />
            <Box
              sx={{
                background: "#349946",
                p: "8px 14px",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
              }}
            >
              {start_date_time
                ? start_date_time?.slice(0, 11)
                : eventPost?.start_date_time?.slice(0, 11)}{" "}
              {start_date_time
                ? start_date_time?.slice(12, 20)
                : eventPost?.start_date_time?.slice(12, 20)}{" "}
              {tzAbbrev ? <span style={{ opacity: 0.9 }}>{tzAbbrev}</span> : null}
            </Box>
          </Box>
          {end_date_time != "undefined," &&
            eventPost?.end_date_time != "undefined," && (
              <Box sx={{ display: "center", alignItems: "center", gap: 1 }}>
                <p style={{ margin: 0 }}>—</p>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      background: "#c71f3c",
                      p: "8px 14px",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {end_date_time
                      ? end_date_time?.slice(0, 11)
                      : eventPost?.end_date_time?.slice(0, 11)}{" "}
                    {end_date_time
                      ? end_date_time?.slice(12, 20)
                      : eventPost?.end_date_time?.slice(12, 20)}{" "}
                    {tzAbbrev ? <span style={{ opacity: 0.9 }}>{tzAbbrev}</span> : null}
                  </Box>
                </Box>
              </Box>
            )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          mt: 1,
        }}
      >
        <img
          src="/Image/Discover/Events/Asset 4.svg"
          alt="free icon"
          style={{ width: "22px" }}
        />
        {free === true || eventPost?.free === true ? (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#000",
            }}
          >
            FREE
          </Typography>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                color: "#000",
                textDecoration:
                  discounted_price !== "" && eventPost?.discountedPrice !== ""
                    ? "line-through"
                    : "none",
              }}
            >
              ₹{pricing ? pricing : eventPost?.price}
            </Typography>
            {discounted_price !== "" && eventPost?.discountedPrice !== "" && (
              <>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px",
                    color: "#000",
                  }}
                >
                  ₹
                  {discounted_price
                    ? discounted_price
                    : eventPost?.discountedPrice}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px",
                    color: "#000",
                  }}
                >
                  {pricing
                    ? `${(((pricing - discounted_price) / pricing) * 100).toFixed(2)}% Off`
                    : `${(((eventPost?.price - eventPost?.discountedPrice) / eventPost?.price) * 100).toFixed(2)}% Off`}
                </Typography>
              </>
            )}
          </>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "16px",
              textAlign: "left",
              color: "#000",
            }}
          >
            Event Description
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "20px",
              textAlign: "left",
              color: "#656565",
              whiteSpace: "pre-wrap",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: showFullDescription ? "none" : 2,
            }}
          >
            {description}
          </Typography>

          {description.length > 100 && (
            <Link
              component="button"
              variant="body2"
              onClick={toggleDescription}
              sx={{
                mt: 0.5,
                color: "#1976d2",
                textDecoration: "none",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {showFullDescription ? "Show less" : "Show more"}
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EventDisplay;
